import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    SignaturePad,
    SignaturePadOptions,
    isDefined,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { generateChecklistCopies } from '../../../store/actions/form.actions';
import {
    getFormEmployeeSignatureFromMaster,
    getFormParticipantsSelectedInParticipantCopiesModal,
    getFormType,
} from '../../../store/selectors/form.selector';
import { FormsState } from '../../../store/state/pace-forms.state';
import { FormsConstants } from '../../../types/forms-constants';
import { Signature } from '../../../types/signature';
import { FormUtilities } from '../../../utilities/form-utilities';

@Component({
    selector: 'pace-copy-checklist-signature',
    templateUrl: './copy-checklist-signature.component.html',
    styleUrls: ['./copy-checklist-signature.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyChecklistSignatureComponent implements OnInit, AfterViewInit {
    @ViewChild('signatureCanvas')
    public signatureCanvas: ElementRef;

    public signaturePad: SignaturePad | undefined;

    public employeeSignature$: Observable<Signature>;

    public signatureForm: FormGroup;
    public firstNameControl: FormControl;
    public lastNameControl: FormControl;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly store: Store<FormsState>,
        private readonly modalService: BsModalService,
    ) {
        this.employeeSignature$ = store.select(getFormEmployeeSignatureFromMaster).pipe(filter(isDefined));
    }

    public ngOnInit(): void {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.firstNameControl = this.formBuilder.control('', [Validators.required]);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.lastNameControl = this.formBuilder.control('', [Validators.required]);

        this.signatureForm = this.formBuilder.group({
            firstName: this.firstNameControl,
            lastName: this.lastNameControl,
        });

        this.employeeSignature$.pipe(take(1)).subscribe((employeeSignature) => {
            // if N A was selected in the master checklist, then we shouldn't set the
            // firs tName and lastName as these would be = "N.A."
            if (employeeSignature.firstName !== FormsConstants.NA) {
                this.firstNameControl.patchValue(employeeSignature.firstName);
            }
            if (employeeSignature.lastName !== FormsConstants.NA) {
                this.lastNameControl.patchValue(employeeSignature.lastName);
            }
        });
    }

    public ngAfterViewInit(): void {
        const signatureOptions: SignaturePadOptions = {
            outputImageType: 'image/jpeg',
            outputImageMaxSize: 9000,
            backgroundColor: 'rgba(255, 255, 255, 1)',
            onEnd: () => {
                this.updateSignatureValidity();
            },
        };

        this.signaturePad = new SignaturePad(
            this.log,
            <HTMLCanvasElement>this.signatureCanvas.nativeElement,
            signatureOptions,
        );

        // The  change detection needs to be delayed to avoid an ExpressionChangedAfterItHasBeenCheckedError
        Promise.resolve(undefined)
            .then(() => {
                this.changeDetectorRef.markForCheck();
            })
            .catch((error: Error) => {
                this.log.error(
                    'CopyChecklistSignatureComponent: ngAfterViewInit: changeDetectorRef.markForCheck: ',
                    JSON.stringify(error),
                );
            });
    }

    public createChecklists(): void {
        if (typeof this.signaturePad === 'undefined') {
            throw new Error(`CopyChecklistSignatureComponent: createChecklists: signaturePad is undefined!`);
        }

        this.store
            .select(getFormParticipantsSelectedInParticipantCopiesModal)
            .pipe(
                take(1),
                map((selectedParticipants) => selectedParticipants.map((p) => p.id)),
                withLatestFrom(
                    this.store.select(getFormType),
                    this.signaturePad.dataUrl$,
                    this.signaturePad.dataPoints$,
                ),
            )
            .subscribe(([selectedParticipantIds, formType, dataUrl, dataPoints]) => {
                this.store.dispatch(
                    generateChecklistCopies({
                        selectedParticipants: selectedParticipantIds,
                        employeeSignatureDataPoints: dataPoints,
                        employeeSignature: dataUrl,
                        employeeFirstName: this.firstNameControl.value as string,
                        employeeLastName: this.lastNameControl.value as string,
                        employeeDate: FormUtilities.isCosimaForm(formType)
                            ? moment().format(ConfigConstants.formatPatternDateCosima)
                            : moment().format(this.clientManager.getDateFormat(false)),
                    }),
                );
                this.modalService.setDismissReason('Created checklists');
                this.modalInstance.hide();
            });
    }

    public clearSignature(): void {
        if (typeof this.signaturePad === 'undefined') {
            throw new Error(
                `CopyChecklistSignatureComponent: clearSignature: Trying to clear non-existent signature pad!`,
            );
        }
        this.signaturePad.clear();

        this.updateSignatureValidity();
    }

    public updateSignatureValidity(): void {
        this.signatureForm.updateValueAndValidity();
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
    }
}
